/**
 * Kundendisplay der SUNMI T3 über die native Bridge: Die App zeigt nur Textzeilen
 * (höchstens 10 à 64 Zeichen, Monospace). Die Zeilen entstehen hier aus demselben
 * `KundenanzeigeStand` wie die Kundenanzeige im zweiten Browserfenster – keine eigene
 * Rechnung, Beträge in Cent aus dem Stand (Regel 5).
 */
import {
  anzeigeZeile,
  formatiereMenge,
  sichtbareZeilen,
  type KundenanzeigeStand,
} from '../kundenanzeige';
import { formatiereEuro, type Position } from '../warenkorb';
import { alsGeraetFehler, type BilanxGeraet, type GeraetFehler } from './bridge';

export const DISPLAY_MAX_ZEILEN = 10;
export const DISPLAY_MAX_ZEICHEN = 64;

/**
 * Zeichen je Zeile am T3-Kundendisplay (1280 × 800, Dichte 160): `ZeilenPresentation` setzt
 * Monospace 30 sp = 30 px, ein Zeichen ist etwa 18 px breit; nutzbar sind 1280 − 2 × 48 px
 * = 1184 px, also rund 65 Zeichen. 52 lässt Reserve für eine größere Systemschrift
 * (Schriftgröße bis etwa 125 %), denn die TextViews kürzen überlange Zeilen ohne „…“.
 */
export const T3_DISPLAY_BREITE = 52;

/** Ab dieser Breite bekommt jede Position eine eigene Spalte „Menge × Einzelpreis“. */
const BREITE_MIT_EINZELPREIS = 40;

export interface DisplayOptionen {
  /** Zeichen je Zeile (Standard 32; höchstens 64). */
  breite?: number;
  /** Zeilen (Standard und höchstens 10). */
  zeilen?: number;
  begruessung?: string;
  /** Trainingsmodus: oberste Zeile „*** TRAINING ***“ in jedem Zustand. */
  training?: boolean;
}

function paar(links: string, rechts: string, breite: number): string {
  const platz = breite - rechts.length - 1;
  const l = links.length > platz ? `${links.slice(0, Math.max(0, platz - 1))}…` : links;
  return `${l}${' '.repeat(Math.max(1, breite - l.length - rechts.length))}${rechts}`.slice(
    0,
    breite,
  );
}

function mitte(text: string, breite: number): string {
  const t = text.slice(0, breite);
  return `${' '.repeat(Math.floor((breite - t.length) / 2))}${t}`;
}

/** Zahlungsstand für den Kunden – ohne interne Prüf- oder Fehlertexte der Kasse. */
const ZAHLTEXT: Record<string, string> = {
  bar: 'Bitte bezahlen',
  karte: 'Bitte am Terminal zahlen',
  beleg: 'Zahlung läuft …',
  pruefung: 'Zahlung läuft …',
  abgelehnt: 'Zahlung abgelehnt',
  abgebrochen: 'Zahlung abgebrochen',
};

export const TRAINING_ZEILE = '*** TRAINING ***';

/** „2 x 1,50“, „0,350 kg x 12,90/kg“, Zeilenrabatt „… -10%“; Bonrabatt ohne Detail. */
function detailText(p: Position): string {
  const a = anzeigeZeile(p);
  if (a.istRabatt) {
    return '';
  }
  const basis = a.kg
    ? a.mengeText.replace(/ €\/kg$/, '/kg').replace(' × ', ' x ')
    : `${formatiereMenge(a.menge)} x ${formatiereEuro(a.einzelCent)}`;
  return a.rabattProzent === null
    ? basis
    : `${basis} -${String(a.rabattProzent).replace('.', ',')}%`;
}

function positionsZeilen(positionen: readonly Position[], breite: number): string[] {
  if (breite < BREITE_MIT_EINZELPREIS) {
    // Schmale Anzeige: „3 x Semmel        1,35“
    return positionen.map((p) => {
      const name = p.menge === 1 ? p.bezeichnung : `${formatiereMenge(p.menge)} x ${p.bezeichnung}`;
      return paar(name, formatiereEuro(p.bruttoCent), breite);
    });
  }
  const details = positionen.map(detailText);
  const betraege = positionen.map((p) => formatiereEuro(p.bruttoCent));
  const bb = Math.max(6, ...betraege.map((b) => b.length));
  // Detailspalte so breit wie nötig, die Bezeichnung behält mindestens 12 Zeichen.
  const db = Math.min(
    Math.max(0, ...details.map((d) => d.length)),
    Math.max(0, breite - bb - 3 - 12),
  );
  return positionen.map((p, i) => {
    const roh = details[i] ?? '';
    // Zu lange Details sichtbar gekürzt („…“), nie still abgeschnitten
    const d = (roh.length > db ? `${roh.slice(0, Math.max(0, db - 1))}…` : roh).padStart(db);
    return paar(p.bezeichnung, `${d}  ${(betraege[i] ?? '').padStart(bb)}`, breite);
  });
}

/** Displayzeilen aus dem Anzeigestand. */
export function kundendisplayZeilen(
  stand: KundenanzeigeStand,
  opt: DisplayOptionen = {},
): string[] {
  const breite = Math.min(DISPLAY_MAX_ZEICHEN, Math.max(16, Math.floor(opt.breite ?? 32)));
  const max = Math.min(
    DISPLAY_MAX_ZEILEN,
    Math.max(4, Math.floor(opt.zeilen ?? DISPLAY_MAX_ZEILEN)),
  );
  const z: string[] = [];
  if (opt.training === true) {
    z.push(mitte(TRAINING_ZEILE, breite));
  }
  if (stand.modus === 'willkommen') {
    z.push('', mitte(opt.begruessung ?? 'Willkommen', breite));
  } else if (stand.modus === 'bezahlt' && stand.bezahlt !== null) {
    const b = stand.bezahlt;
    z.push(paar('Summe EUR', formatiereEuro(b.summeCent), breite));
    if (b.gutscheinCent > 0) {
      z.push(paar('Gutschein', `-${formatiereEuro(b.gutscheinCent)}`, breite));
    }
    if (b.gegebenCent !== null) {
      z.push(paar('Bar erhalten', formatiereEuro(b.gegebenCent), breite));
    }
    if (b.rueckgeldCent !== null) {
      z.push(paar('Rückgeld', formatiereEuro(b.rueckgeldCent), breite));
    }
    if (b.trinkgeldCent > 0) {
      z.push(paar('Trinkgeld', formatiereEuro(b.trinkgeldCent), breite));
    }
    z.push('', mitte('Vielen Dank!', breite));
  } else {
    // Bon: neueste Positionen, darunter Summe und ggf. Zahlstand
    const trenner = breite >= BREITE_MIT_EINZELPREIS;
    const fuss =
      z.length +
      1 +
      (trenner ? 1 : 0) +
      (stand.gutscheinCent > 0 ? 1 : 0) +
      (stand.zahlung !== null ? 1 : 0);
    const { zeilen, ausgeblendet } = sichtbareZeilen(stand.positionen, max - fuss);
    if (ausgeblendet > 0) {
      z.push(`… ${String(ausgeblendet)} weitere`);
    }
    const sichtbar = stand.positionen.slice(stand.positionen.length - zeilen.length);
    z.push(...positionsZeilen(sichtbar, breite));
    if (trenner) {
      z.push('-'.repeat(breite));
    }
    if (stand.gutscheinCent > 0) {
      z.push(paar('Gutschein', `-${formatiereEuro(stand.gutscheinCent)}`, breite));
    }
    // summeCent ist im Bon bereits der Betrag nach Gutschein (wie in der Kundenanzeige)
    z.push(
      paar(
        stand.gutscheinCent > 0 ? 'ZU ZAHLEN EUR' : 'SUMME EUR',
        formatiereEuro(stand.summeCent),
        breite,
      ),
    );
    if (stand.zahlung !== null) {
      z.push(ZAHLTEXT[stand.zahlung.status] ?? '');
    }
  }
  return z.slice(0, max).map((s) => s.slice(0, breite));
}

/**
 * Zeilen ans Display schicken. Gibt den Fehler zurück statt zu werfen – das Display ist
 * Beiwerk und darf den Verkauf nie aufhalten.
 */
export async function zeigeAmKundendisplay(
  geraet: BilanxGeraet,
  zeilen: readonly string[],
): Promise<GeraetFehler | null> {
  try {
    await geraet.kundendisplay(
      zeilen.slice(0, DISPLAY_MAX_ZEILEN).map((s) => s.slice(0, DISPLAY_MAX_ZEICHEN)),
    );
    return null;
  } catch (e) {
    return alsGeraetFehler(e);
  }
}

/**
 * Schickt nur geänderte Inhalte (Bon-Änderungen kommen bei jedem Tastendruck) und höchstens
 * einen Auftrag gleichzeitig; der jeweils neueste Stand gewinnt.
 */
export class KundendisplaySender {
  private zuletzt: string | null = null;
  private laeuft = false;
  private wartend: string[] | null = null;
  private gestoppt = false;

  constructor(
    private readonly geraet: BilanxGeraet,
    private readonly opt: DisplayOptionen = {},
  ) {}

  /** Abbau: Wartendes verwerfen, keine neuen Aufträge mehr (ein laufender endet von selbst). */
  stoppe(): void {
    this.gestoppt = true;
    this.wartend = null;
  }

  private istGestoppt(): boolean {
    return this.gestoppt;
  }

  /** Nach [stoppe] wieder senden (z. B. StrictMode-Neustart). */
  fortsetzen(): void {
    this.gestoppt = false;
  }

  /** Läuft gerade ein Auftrag? Sein Aufrufer erfährt am Ende den neuesten Stand. */
  get beschaeftigt(): boolean {
    return this.laeuft;
  }

  /** Wurden genau diese Zeilen zuletzt vom Gerät bestätigt? */
  angezeigt(zeilen: readonly string[]): boolean {
    return this.zuletzt === JSON.stringify(zeilen);
  }

  zeige(stand: KundenanzeigeStand): Promise<void> {
    return this.sende(kundendisplayZeilen(stand, this.opt));
  }

  async sende(zeilen: string[]): Promise<void> {
    // Läuft ein Auftrag, gewinnt immer der zuletzt verlangte Stand – auch wenn er dem
    // zuletzt gesendeten gleicht (A → B läuft → A muss wieder A zeigen; Codex Fund 12).
    if (this.gestoppt) {
      return;
    }
    if (this.laeuft) {
      this.wartend = zeilen;
      return;
    }
    if (JSON.stringify(zeilen) === this.zuletzt) {
      return;
    }
    this.laeuft = true;
    try {
      let aktuell: string[] | null = zeilen;
      while (aktuell !== null) {
        const k = JSON.stringify(aktuell);
        if (k !== this.zuletzt) {
          const f = await zeigeAmKundendisplay(this.geraet, aktuell);
          // Bei Fehler nicht merken: der nächste Stand versucht es erneut
          this.zuletzt = f === null ? k : null;
        }
        // stoppe() kann während des await gekommen sein
        aktuell = this.istGestoppt() ? null : this.wartend;
        this.wartend = null;
      }
    } finally {
      this.laeuft = false;
    }
  }
}
